import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Input } from './ui/input';
import { Plane } from 'lucide-react';
import { createPlacesSession } from '../lib/placesSession';
import { isTypedCode, wasChosenAlready, failureHint } from '../lib/airportSuggest';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const t = localStorage.getItem('rideburaq_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Address input with Google Places autocomplete, proxied through our backend
 * (/api/places/*) so the Google key stays server-side. Falls back to plain text
 * entry when the backend returns no suggestions (e.g. key not configured).
 */
const GooglePlacesInput = ({ value, onChange, onSelect, placeholder, className, disabled = false, onBlurText = null, inputTestId = 'address-autocomplete-input' }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const sessionRef = useRef(null);
  if (sessionRef.current === null) sessionRef.current = createPlacesSession();   // one session manager per address box (pickup, drop-off, each stop)
  const debounceRef = useRef(null);
  const abortRef = useRef(null);           // the suggestion request in flight: a new keystroke cancels it
  const latestRef = useRef('');            // what is in the box now: a late answer for older text is ignored
  const chosenRef = useRef('');            // the text of the last pick, so leaving the box does not look it up again
  const [hint, setHint] = useState('');
  const chooseRef = useRef(null);

  const positionDropdown = useCallback(() => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setRect({ left: r.left, top: r.bottom + 4, width: r.width });
    }
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || !q.trim()) { setSuggestions([]); setOpen(false); setHint(''); return; }      // suggestions start at the FIRST character
    if (abortRef.current) abortRef.current.abort();
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortRef.current = ctl;
    try {
      const res = await fetch(
        `${BACKEND}/api/places/autocomplete?q=${encodeURIComponent(q.trim())}&session=${sessionRef.current.get()}`,
        { headers: { ...authHeader() }, ...(ctl ? { signal: ctl.signal } : {}) }
      );
      if (latestRef.current !== q) return;                                                // the visitor kept typing: this answer is stale
      if (!res.ok) { setSuggestions([]); setOpen(false); setHint(failureHint(res.status)); return; }
      const data = await res.json();
      const list = data.suggestions || [];
      setSuggestions(list);
      setActiveIdx(-1);
      setHint(list.length ? '' : failureHint(200, data.googleOk));
      if (list.length) { positionDropdown(); setOpen(true); } else { setOpen(false); }
      if (isTypedCode(q, list[0]) && chooseRef.current) chooseRef.current(list[0]);                                       // "jfk" selects the airport by itself
    } catch (e) { if (e && e.name === 'AbortError') return; setOpen(false); setHint(failureHint(503)); }       // a cancelled request is not a failure
  // eslint-disable-next-line
  }, [positionDropdown]);

  const handleChange = (v) => {
    onChange(v);
    latestRef.current = v;               // this runs only when the visitor types, so every keystroke (also right after a pick) asks for suggestions
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), v.trim().length <= 3 ? 120 : 250);
  };

  const choose = async (s) => {
    setOpen(false);
    setSuggestions([]);
    setHint('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    if (s.airport) {                                    // our own airport list: name and coordinates are already here, no Google call
      const text = s.address || s.description;
      chosenRef.current = text;
      latestRef.current = text;
      onChange(text);
      if (onSelect) onSelect({ address: text, lat: s.lat, lng: s.lng, airport: s.code });
      sessionRef.current.end();
      return;
    }
    onChange(s.description);
    chosenRef.current = s.description;
    latestRef.current = s.description;
    const sessionToken = sessionRef.current.get();     // the SAME token the suggestions used, so Google closes the session on this call
    try {
      const res = await fetch(
        `${BACKEND}/api/places/details?place_id=${encodeURIComponent(s.placeId)}&session=${sessionToken}`,
        { headers: { ...authHeader() } }
      );
      const d = await res.json();
      if (d && d.address) { onChange(d.address); chosenRef.current = d.address; latestRef.current = d.address; }
      if (onSelect) onSelect({ address: (d && d.address) || s.description, lat: d?.lat ?? null, lng: d?.lng ?? null });
    } catch (e) {
      if (onSelect) onSelect({ address: s.description, lat: null, lng: null });
    }
    sessionRef.current.end();                          // the next search starts a fresh session
  };

  chooseRef.current = choose;                           // always the latest handlers

  useEffect(() => {
    const onDown = (e) => { const inside = (wrapRef.current && wrapRef.current.contains(e.target)) || (listRef.current && listRef.current.contains(e.target)); if (!inside) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const reposition = () => positionDropdown();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, positionDropdown]);

  // Leaving the box with an airport name typed but not picked ("newark airport"): resolve it so the airport details appear.
  const resolveOnBlur = async () => {
    const v = (value || '').trim();
    if (!v || v.length > 60 || wasChosenAlready(v, chosenRef.current)) return;
    try {
      const res = await fetch(`${BACKEND}/api/airports/resolve?q=${encodeURIComponent(v)}`, { headers: { ...authHeader() } });
      if (!res.ok) return;
      const j = await res.json();
      if (j && j.airport) {
        const text = j.airport.address;
        chosenRef.current = text; latestRef.current = text;
        onChange(text);
        if (onSelect) onSelect({ address: text, lat: j.airport.lat, lng: j.airport.lng, airport: j.airport.code });
      }
    } catch (e) { /* the booking itself also resolves airport names on the server */ }
  };

  const onKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { if (activeIdx >= 0) { e.preventDefault(); choose(suggestions[activeIdx]); } }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="w-full">
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => { if (suggestions.length) { positionDropdown(); setOpen(true); } }}
        onKeyDown={onKeyDown}
        onBlur={() => { setTimeout(resolveOnBlur, 150); if (onBlurText) onBlurText(); }}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        autoComplete="off"
        data-testid={inputTestId}
      />
      {hint && !open && <p data-testid="address-hint" className="text-xs text-amber-300/90 mt-1">{hint}</p>}
      {open && suggestions.length > 0 && rect && createPortal(
        <div
          ref={listRef}
          className="fixed z-[9999] bg-neutral-900 border border-white/15 rounded-lg shadow-2xl max-h-72 overflow-y-auto"
          style={{ left: rect.left, top: rect.top, width: rect.width }}
          data-testid="address-suggestions"
        >
          {suggestions.map((s, i) => (
            <button
              key={s.placeId}
              data-airport={s.airport ? s.code : undefined}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              className={`w-full text-left px-4 py-3 text-sm text-white hover:bg-orange-500/20 ${i === activeIdx ? 'bg-orange-500/20' : ''}`}
              data-testid={`address-suggestion-${i}`}
            >
              {s.airport && <Plane className="inline h-4 w-4 mr-2 -mt-0.5 text-orange-400" aria-hidden="true" />}
              {s.description}
            </button>
          ))}
        </div>
      , document.body)}
    </div>
  );
};

export default GooglePlacesInput;
